import { Layers } from 'lucide-react';
import { AREAS_CONHECIMENTO } from '../materias/catalogo-bncc-data';
import { MateriaEditSidePanel } from './MateriaEditSidePanel';
import type { Materia } from '../../types/models';

export const MATERIA_COR_PRESETS = [
  '#3b82f6', '#8b5cf6', '#10b981', '#f97316', '#ec4899',
  '#f59e0b', '#06b6d4', '#84cc16', '#dc2626', '#0891b2',
];

export type MateriaFormState = {
  nome: string;
  codigo: string;
  areaDoCOnhecimento: string;
  siglaBNCC: string;
  corVisual: string;
  obrigatoria: boolean;
  etapasAplicaveis: string[];
};

type Props = {
  form: MateriaFormState;
  setForm: React.Dispatch<React.SetStateAction<MateriaFormState>>;
  editing: Materia | null;
};

function HeroBlock({ form, setForm }: Pick<Props, 'form' | 'setForm'>) {
  return (
    <>
      <div
        className="ac-materia-modal__stripe"
        style={{ background: form.corVisual || '#94a3b8' }}
        aria-hidden
      />
      <div className="ac-materia-modal__hero">
        <div
          className="ac-materia-modal__hero-color"
          style={{ '--materia-accent': form.corVisual } as React.CSSProperties}
        >
          <div className="ac-materia-modal__swatch-wrap">
            <span
              className="ac-materia-modal__swatch"
              style={{ background: form.corVisual }}
              aria-hidden
            />
            <div className="ac-materia-modal__swatch-meta">
              <span className="ac-materia-modal__swatch-label">Cor na grade</span>
              <span className="ac-materia-modal__swatch-hex">{form.corVisual}</span>
            </div>
          </div>
          <div className="ac-materia-modal__presets" role="group" aria-label="Cores rápidas">
            {MATERIA_COR_PRESETS.map((c) => (
              <button
                key={c}
                type="button"
                className={`ac-materia-modal__preset${form.corVisual === c ? ' ac-materia-modal__preset--active' : ''}`}
                style={{ background: c }}
                onClick={() => setForm((f) => ({ ...f, corVisual: c }))}
                title={c}
              />
            ))}
          </div>
          <input
            type="color"
            className="ac-materia-modal__color-picker"
            value={form.corVisual}
            onChange={(e) => setForm((f) => ({ ...f, corVisual: e.target.value }))}
            aria-label="Seletor de cor personalizada"
          />
        </div>

        <div className="ac-materia-modal__hero-fields">
          <label className="ac-materia-field">
            <span className="ac-materia-field__label">Nome da matéria *</span>
            <input
              className="ac-materia-field__input ac-materia-field__input--lg"
              value={form.nome}
              onChange={(e) => setForm((f) => ({ ...f, nome: e.target.value }))}
              placeholder="Ex.: Matemática"
              autoFocus
            />
          </label>
          <div className="ac-materia-modal__hero-row">
            <label className="ac-materia-field">
              <span className="ac-materia-field__label">Código / sigla</span>
              <input
                className="ac-materia-field__input"
                value={form.codigo}
                onChange={(e) => setForm((f) => ({ ...f, codigo: e.target.value }))}
                placeholder="MAT"
              />
            </label>
            <label className="ac-materia-field">
              <span className="ac-materia-field__label">Sigla BNCC</span>
              <input
                className="ac-materia-field__input"
                value={form.siglaBNCC}
                onChange={(e) => setForm((f) => ({ ...f, siglaBNCC: e.target.value }))}
                placeholder="MA"
              />
            </label>
          </div>
        </div>
      </div>
    </>
  );
}

function ClassificacaoSection({ form, setForm }: Pick<Props, 'form' | 'setForm'>) {
  return (
    <section className="ac-materia-section">
      <div className="ac-materia-section__head">
        <Layers size={14} />
        <h3 className="ac-materia-section__title">Classificação</h3>
      </div>
      <div className="ac-materia-section__grid ac-materia-section__grid--area">
        <label className="ac-materia-field">
          <span className="ac-materia-field__label">Área do conhecimento</span>
          <select
            className="ac-materia-field__select"
            value={form.areaDoCOnhecimento}
            onChange={(e) => setForm((f) => ({ ...f, areaDoCOnhecimento: e.target.value }))}
          >
            <option value="">Selecione a área (opcional)</option>
            {AREAS_CONHECIMENTO.map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </select>
        </label>
        <label className="ac-materia-toggle">
          <input
            type="checkbox"
            checked={form.obrigatoria}
            onChange={(e) => setForm((f) => ({ ...f, obrigatoria: e.target.checked }))}
          />
          <span className="ac-materia-toggle__text">
            <strong>Matéria obrigatória</strong>
            <span>Integra a Base Nacional Comum — exibida na listagem e relatórios.</span>
          </span>
        </label>
      </div>
    </section>
  );
}

export function MateriasFormModal({ form, setForm, editing }: Props) {
  if (editing) {
    return (
      <div className="ac-modal-dual ac-materia-modal-dual">
        <HeroBlock form={form} setForm={setForm} />
        <div className="ac-modal-dual__columns">
          <div className="ac-modal-dual__main">
            <ClassificacaoSection form={form} setForm={setForm} />
          </div>
          <div className="ac-modal-dual__divider" aria-hidden />
          <MateriaEditSidePanel materia={editing} />
        </div>
      </div>
    );
  }

  return (
    <div className="ac-materia-modal-dual">
      <HeroBlock form={form} setForm={setForm} />
      <div className="ac-materia-modal__body ac-materia-modal__body--solo">
        <p className="ac-materia-modal__intro">
          Defina identidade visual e classificação. Professores e grade são vinculados depois.
        </p>
        <ClassificacaoSection form={form} setForm={setForm} />
      </div>
    </div>
  );
}
